import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Building2, 
  Users, 
  FileText, 
  MessageSquare, 
  LogOut, 
  LayoutDashboard,
  ChevronRight,
  X,
  Clock,
  FileSignature,
  BookTemplate,
  BarChart3,
  ClipboardList,
  Globe,
  Inbox
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout, isAdmin, isCompany, isWorker } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      onClose?.();
    }
  };

  const NavItem = ({ to, icon: Icon, children, end }) => (
    <NavLink
      to={to}
      end={end}
      onClick={handleNavClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all duration-200 rounded-lg ${
          isActive
            ? 'bg-orange-50 text-orange-600 border border-orange-100'
            : 'text-gray-600 hover:bg-orange-50/50 hover:text-orange-600'
        }`
      }
    >
      <Icon className="w-5 h-5" />
      <span>{children}</span>
      <ChevronRight className="w-4 h-4 ml-auto opacity-40" />
    </NavLink>
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 z-50 h-screen w-64 bg-white border-r border-orange-100
          flex flex-col transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className="p-4 lg:p-6 border-b border-orange-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md shadow-orange-200">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900 text-base">
                  Urcalportal
                </h2>
                <p className="text-xs text-orange-500 font-medium uppercase tracking-wider">
                  {isAdmin ? 'Admin' : isCompany ? 'Empresa' : 'Trabajador'}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="lg:hidden p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {isAdmin && (
            <>
              <NavItem to="/admin" icon={LayoutDashboard} end>Dashboard</NavItem>
              <NavItem to="/admin/companies" icon={Building2}>Empresas</NavItem>
              <NavItem to="/admin/requests" icon={Inbox}>Solicitudes</NavItem>
            </>
          )}

          {isCompany && (
            <>
              <NavItem to="/company" icon={LayoutDashboard} end>Dashboard</NavItem>
              <NavItem to="/company/workers" icon={Users}>Trabajadores</NavItem>
              <NavItem to="/company/time-records" icon={Clock}>Control Horario</NavItem>
              <NavItem to="/company/documents" icon={FileText}>Documentos</NavItem>
              <NavItem to="/company/signable-documents" icon={FileSignature}>Docs. Firmables</NavItem>
              <NavItem to="/company/communications" icon={MessageSquare}>Comunicados</NavItem>
              <NavItem to="/company/templates" icon={BookTemplate}>Plantillas</NavItem>
              <NavItem to="/company/analytics" icon={BarChart3}>Estadisticas</NavItem>
              <NavItem to="/company/daily-report" icon={ClipboardList}>Informe Diario</NavItem>
              <NavItem to="/company/work-reports" icon={ClipboardList}>Partes Trabajo</NavItem>
              <NavItem to="/company/portal-settings" icon={Globe}>Portal Publico</NavItem>
            </>
          )}

          {isWorker && (
            <>
              <NavItem to="/worker" icon={LayoutDashboard} end>Dashboard</NavItem>
              <NavItem to="/worker/time-clock" icon={Clock}>Fichar</NavItem>
              <NavItem to="/worker/work-reports" icon={ClipboardList}>Partes Trabajo</NavItem>
              <NavItem to="/worker/documents" icon={FileText}>Mis Documentos</NavItem>
              <NavItem to="/worker/signable-documents" icon={FileSignature}>Docs. para Firmar</NavItem>
              <NavItem to="/worker/communications" icon={MessageSquare}>Comunicados</NavItem>
            </>
          )}
        </nav>

        {/* User info & logout */}
        <div className="p-4 border-t border-orange-100">
          <div className="mb-4 px-2">
            <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            {user?.company_name && (
              <p className="text-xs text-orange-400 truncate mt-1">{user.company_name}</p>
            )}
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full justify-start gap-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 border-orange-100 hover:border-orange-200"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Cerrar Sesion
          </Button>
        </div>
      </aside>
    </>
  );
}
